import type { ClubRole } from "@rivaled/shared";
import { keepPreviousData } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ChevronLeft,
  ChevronRight,
  Crown,
  Gavel,
  MoreVertical,
  Shield,
  UserMinus,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { usePlatformRole } from "../../hooks/usePlatformRole";
import { canClubRolePermission } from "../../lib/permissions";
import { trpc } from "../../lib/trpc";
import {
  Avatar,
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  SearchInput,
} from "../ui";
import { ConfirmModal } from "./ConfirmModal";
import { roleConfig } from "./types";

const ITEMS_PER_PAGE = 20;

const roleIconColor: Record<string, string> = {
  owner: "text-brand-400",
  admin: "text-purple-400",
  moderator: "text-blue-400",
  member: "text-surface-400",
};

const roleFilters = [
  { value: undefined, label: "All" },
  { value: "owner" as const, label: "Owner" },
  { value: "admin" as const, label: "Admin" },
  { value: "moderator" as const, label: "Mod" },
  { value: "member" as const, label: "Member" },
];

type Props = {
  clubId: string;
  clubSlug: string;
  isOwner: boolean;
  myRole: string;
};

export function MembersSection({ clubId, clubSlug, isOwner, myRole }: Props) {
  const utils = trpc.useUtils();
  const { isAdmin: isPlatformAdmin, isModerator: isPlatformModerator } = usePlatformRole();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleRoleFilter = (value: string | undefined) => {
    setRoleFilter(value);
    setPage(1);
  };

  const { data, isLoading, isFetching } = trpc.clubMember.list.useQuery(
    {
      clubId,
      limit: ITEMS_PER_PAGE,
      page,
      role: roleFilter as "owner" | "admin" | "moderator" | "member" | undefined,
      search: debouncedSearch || undefined,
    },
    { placeholderData: keepPreviousData },
  );
  const members = data?.items ?? [];

  const [kickTarget, setKickTarget] = useState<{ id: string; username: string } | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ id: string; username: string } | null>(
    null,
  );

  const updateRoleMutation = trpc.clubMember.updateRole.useMutation({
    onSuccess: () => {
      toast.success("Role updated");
      void utils.clubMember.list.invalidate({ clubId });
    },
    onError: (err) => toast.error(err.message || "Failed to update role"),
  });

  const kickMutation = trpc.clubMember.kick.useMutation({
    onSuccess: () => {
      toast.success("Member kicked");
      void utils.clubMember.list.invalidate({ clubId });
      void utils.club.get.invalidate();
      setKickTarget(null);
    },
    onError: (err) => toast.error(err.message || "Failed to kick member"),
  });

  const transferMutation = trpc.clubMember.transferOwnership.useMutation({
    onSuccess: () => {
      toast.success("Ownership transferred");
      void utils.clubMember.list.invalidate({ clubId });
      void utils.club.getMyClubs.invalidate();
      setTransferTarget(null);
    },
    onError: (err) => toast.error(err.message || "Failed to transfer ownership"),
  });

  const clubRole = myRole as ClubRole;
  const roleHierarchy: Record<string, number> = { owner: 3, admin: 2, moderator: 1, member: 0 };
  const myLevel = roleHierarchy[myRole] ?? 0;
  const canViewModerationHistory =
    canClubRolePermission(clubRole, {
      resource: "dispute",
      actions: ["view"],
    }) || isPlatformModerator;

  // Can we show the dropdown at all for this member? (kick requires managing lower roles)
  const canManageMember = (targetRole: string) => {
    if (isPlatformModerator) {
      return targetRole !== "owner";
    }
    if (targetRole === "owner") return false;
    return myLevel > (roleHierarchy[targetRole] ?? 0);
  };

  // Can we change this member's role? (mods cannot)
  const canKickMember = (targetRole: string) =>
    canManageMember(targetRole) &&
    (canClubRolePermission(clubRole, {
      resource: "member",
      actions: ["kick"],
    }) ||
      isPlatformModerator);
  const canChangeRole = (targetRole: string) =>
    canManageMember(targetRole) &&
    (canClubRolePermission(clubRole, {
      resource: "member",
      actions: ["set_role"],
    }) ||
      isPlatformModerator);
  const canTransferOwnership = (targetRole: string) =>
    targetRole !== "owner" &&
    (canClubRolePermission(clubRole, {
      resource: "club",
      actions: ["transfer"],
    }) ||
      isPlatformAdmin);
  const shouldShowActionsMenu = (targetRole: string) =>
    canViewModerationHistory ||
    canKickMember(targetRole) ||
    canChangeRole(targetRole) ||
    canTransferOwnership(targetRole);

  const roleOptions =
    isOwner || isPlatformAdmin ? ["admin", "moderator", "member"] : ["moderator", "member"];

  return (
    <div className="space-y-4">
      {/* Search + Role filter bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <SearchInput
          placeholder="Search members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch("")}
          loading={isFetching && !!debouncedSearch}
          className="flex-1"
        />
        <div className="flex items-center gap-1 shrink-0">
          {roleFilters.map((rf) => (
            <button
              key={rf.label}
              type="button"
              onClick={() => handleRoleFilter(rf.value)}
              className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                roleFilter === rf.value
                  ? "bg-brand-500/15 text-brand-400 border border-brand-500/30"
                  : "text-surface-400 hover:text-surface-200 hover:bg-surface-800/60 border border-transparent"
              }`}
            >
              {rf.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs font-medium text-surface-400">
        {data ? (
          <>
            {data.total} member{data.total !== 1 ? "s" : ""}
            {(debouncedSearch || roleFilter) && " found"}
          </>
        ) : (
          "\u00A0"
        )}
      </p>

      {/* Member list */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-14 rounded-xl bg-surface-800/50 animate-pulse" />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-surface-500">
          <Users className="size-8 mb-2 opacity-40" />
          <p className="text-sm">
            {debouncedSearch || roleFilter ? "No members match your filters" : "No members yet"}
          </p>
        </div>
      ) : (
        <div className="space-y-1">
          {members.map((member) => {
            const role = roleConfig[member.role] ?? roleConfig.member;
            return (
              <div
                key={member.id}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-800/40 transition-colors"
              >
                <div className="relative shrink-0">
                  <Avatar
                    size="sm"
                    variant="rounded"
                    src={member.avatarUrl}
                    initials={member.username.charAt(0)}
                    alt={member.username}
                  />
                  {member.role === "owner" && (
                    <span className="absolute -top-1 -right-1">
                      <Crown className="size-3 text-yellow-400" />
                    </span>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{member.username}</p>
                </div>
                <Badge variant="subtle" color={role.color} size="sm">
                  {role.label}
                </Badge>

                {shouldShowActionsMenu(member.role) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="flex items-center justify-center size-7 rounded-lg text-surface-400 hover:text-white hover:bg-surface-700/40 transition-colors shrink-0"
                      >
                        <MoreVertical className="size-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" sideOffset={6} className="min-w-40">
                      {canViewModerationHistory && (
                        <>
                          <DropdownMenuItem asChild>
                            <Link
                              to="/app/clubs/$slug/admin/moderation"
                              params={{ slug: clubSlug }}
                              search={{ userId: member.userId, username: member.username }}
                            >
                              <Gavel className="size-4 text-brand-400" />
                              Moderation History
                            </Link>
                          </DropdownMenuItem>
                          {canManageMember(member.role) && <DropdownMenuSeparator />}
                        </>
                      )}
                      {canChangeRole(member.role) && (
                        <>
                          <DropdownMenuLabel>Change role</DropdownMenuLabel>
                          {roleOptions.map((r) => (
                            <DropdownMenuItem
                              key={r}
                              disabled={member.role === r}
                              onClick={() =>
                                updateRoleMutation.mutate({
                                  clubId,
                                  userId: member.userId,
                                  role: r,
                                })
                              }
                            >
                              <Shield
                                className={`size-4 ${roleIconColor[r] ?? "text-surface-400"}`}
                              />
                              {roleConfig[r]?.label ?? r}
                              {member.role === r && (
                                <span className="ml-auto text-2xs text-surface-500">Current</span>
                              )}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                        </>
                      )}
                      {canTransferOwnership(member.role) && (
                        <DropdownMenuItem
                          onClick={() =>
                            setTransferTarget({
                              id: member.userId,
                              username: member.username,
                            })
                          }
                        >
                          <Crown className="size-4 text-yellow-400" />
                          Transfer Ownership
                        </DropdownMenuItem>
                      )}
                      {canKickMember(member.role) && (
                        <DropdownMenuItem
                          className="text-error-400 focus:text-error-400"
                          onClick={() =>
                            setKickTarget({ id: member.userId, username: member.username })
                          }
                        >
                          <UserMinus className="size-4" />
                          Kick Member
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between pt-2 border-t border-surface-700/50">
          <p className="text-xs text-surface-500">
            Page {data.page} of {data.totalPages}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="!px-2"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="!px-2"
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {kickTarget && (
        <ConfirmModal
          open={true}
          onClose={() => setKickTarget(null)}
          title="Kick Member"
          description={`Are you sure you want to kick ${kickTarget.username} from the club?`}
          confirmLabel="Kick"
          isPending={kickMutation.isPending}
          variant="danger"
          icon={<UserMinus className="size-5 text-error-400" />}
          onConfirm={() => kickMutation.mutate({ clubId, userId: kickTarget.id })}
        />
      )}

      {transferTarget && (
        <ConfirmModal
          open={true}
          onClose={() => setTransferTarget(null)}
          title="Transfer Ownership"
          description={`Transfer club ownership to ${transferTarget.username}? You will become an admin.`}
          confirmLabel="Transfer"
          isPending={transferMutation.isPending}
          variant="warning"
          icon={<Crown className="size-5 text-yellow-400" />}
          onConfirm={() => transferMutation.mutate({ clubId, newOwnerId: transferTarget.id })}
        />
      )}
    </div>
  );
}
